import { useEffect, useRef, useCallback } from "react";
import useRawSWRSubscriptions, {
  type SWRSubscriptionResponse,
} from "swr/subscription";
import type { Key, SWRHook, Middleware } from "swr";
import type { ZodTypeAny, infer as zInfer } from "zod";
import { createSubscribe, type Options } from "hooks/firestore/v2/subscribe";
import type { QueryConstraint } from "firebase/firestore";
import { useSelector } from "hooks/useSelector";

const loading: Middleware =
  (useSWRNext: SWRHook) => (key, subscriber, config) => {
    if (!subscriber) {
      return useSWRNext(key, config);
    }

    const swr = useSWRNext(key, subscriber, config);

    // when data is undefined or key has changed, we are loading
    // if there is no key, consider it finished loading
    const isLoading = key ? typeof swr.data === "undefined" : false;

    return Object.assign({}, swr, {
      data: swr.data,
      isLoading,
    });
  };

const laggyValue: Middleware =
  (useSWRNext: SWRHook) => (key, subscriber, config) => {
    // save previous value as ref and compare it to the new value
    const laggyDataRef = useRef<typeof swr.data>();

    // Actual SWR hook.
    const swr = useSWRNext(key, subscriber, config);

    useEffect(() => {
      // Update ref if data is not undefined.
      if (swr.data !== undefined) {
        laggyDataRef.current = swr.data;
      }
    }, [swr.data]);

    const resetLaggy = useCallback(() => {
      laggyDataRef.current = undefined;
    }, []);

    const dataOrLaggyData =
      swr.data === undefined && Boolean(key) ? laggyDataRef.current : swr.data;

    const isLagging =
      swr.data === undefined && laggyDataRef.current !== undefined;

    return Object.assign({}, swr, {
      data: dataOrLaggyData,
      isLagging,
      resetLaggy,
    });
  };

const makeKey = (
  key: Key,
  constraints: QueryConstraint[] | undefined,
  userId: string,
) => {
  if (typeof key !== "string") return;

  if (!key) return;

  // key has userId, but we have no session, quit early
  if (key.includes("{userId}") && !userId) return;

  key = key.replace("{userId}", userId);

  if (!constraints) {
    return key;
  }

  return {
    key,
    constraints: JSON.stringify(constraints),
  };
};

/**
 * We have an optional property called `keyPath` on the options object and when
 * this is provided there is some magic behind the scenes that appends it as a
 * property onto the data object(s) returned. However, if we didn't manually add
 * this property to the model definition ourselves it wouldn't be typed. This
 * type automatically appends whatever string we pass to the `keyPath` property
 * onto the data returned so we no longer have to manually include it.
 */
type ExtendedSWRSubscriptionResponse<
  TModel extends ZodTypeAny,
  TKeyPath extends string | undefined,
  TKeyPathValue = string,
> = SWRSubscriptionResponse<
  TKeyPath extends string
    ? TModel extends Array<any>
      ? // If the model is an array schema we need to add the `keyPath` property to each element in the array
        Array<TModel[number] & { [K in TKeyPath]: TKeyPathValue }>
      : // If the model is not an array we just add the `keyPath` directly to the object
        TModel & { [K in TKeyPath]: TKeyPathValue }
    : TModel
>;

const useFirestore = <
  TModel extends ZodTypeAny,
  TKeyPath extends string | undefined,
  TKeyPathValue = string,
>(
  key: Key,
  model: TModel,
  options: Options<TKeyPath> = {},
) => {
  const userId = useSelector((state) => state.auth.userId);

  const swrResponse = useRawSWRSubscriptions(
    makeKey(key, options?.query, userId),
    createSubscribe(model, options),
    {
      use: [loading, laggyValue],
    },
  );

  return swrResponse as ExtendedSWRSubscriptionResponse<
    zInfer<TModel>,
    TKeyPath,
    TKeyPathValue
  > & {
    isLoading: boolean;
  };
};

export { useFirestore };
